<template>
  <!-- A native button, not TwnBaseBtn: an option is a full-width, left-aligned row in a listbox, and
       TwnBaseBtn's centred PrimeVue chrome would have to be overridden with `!important` to fit. -->
  <button
    :aria-selected="isSelected"
    :class="rowClass"
    :data-key="optionKey"
    :disabled="disabled"
    class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors duration-100"
    role="option"
    tabindex="-1"
    type="button"
    @click="emit('select')"
    @mouseenter="onHover"
  >
    <span
      :class="resolvedBadgeClass"
      class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
    >
      <MaterialIcon
        :ligature="icon"
        size="1.125rem"
      />
    </span>
    <span class="flex min-w-0 flex-1 flex-col">
      <span
        :class="labelClass"
        class="truncate"
      >
        {{ label }}
      </span>
      <span
        v-if="subLabel"
        class="truncate text-xs text-surface-500"
      >
        {{ subLabel }}
      </span>
    </span>
    <slot name="trailing" />
  </button>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type {
  TwnPaletteOptionEmits,
  TwnPaletteOptionProps
} from '@/components/molecules/palette/twn-palette-option/TwnPaletteOption.types'

import MaterialIcon from '@/components/atoms/icons/material-icon/MaterialIcon.vue'

const props = withDefaults(defineProps<TwnPaletteOptionProps>(), {
  subLabel: undefined,
  active: false,
  disabled: false,
  danger: false,
  badgeClass: undefined
})

const emit = defineEmits<TwnPaletteOptionEmits>()

// A disabled row is never the highlighted one, even while its key is.
const isSelected = computed(() => props.active && !props.disabled)

const rowClass = computed(() => {
  if (props.disabled) return 'cursor-default opacity-45'

  return props.active ? 'bg-primary-50' : 'hover:bg-surface-100'
})

const resolvedBadgeClass = computed(() => {
  if (props.badgeClass) return props.badgeClass

  return props.danger ? 'bg-red-100 text-red-600' : 'bg-surface-100 text-surface-600'
})

const labelClass = computed(() => (props.danger ? 'text-red-600' : 'text-surface-900'))

const onHover = (): void => {
  if (!props.disabled) emit('hover')
}
</script>
